import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  HostListener,
  Inject,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { map, Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PageUtils } from '../../../common/page.utils';
import { TestletType } from '../../../constants/testlet-type.enum';
import { APP_CONFIG, AppConfig } from '../../../models/app-config';
import { AssessmentAttemptedStatus } from '../../../models/assessment-models';
import { AssessmentPlayerService } from '../../../services/player.service';
import { noNav } from '../../../store/actions/page-content.actions';
import { PageContentState } from '../../../store/models/assessment-models';
import { selectCurrentUpdateAssessmentState } from '../../../store/selectors/assessment.selectors';
import { selectActionButtons, selectDisplayActionButtons } from '../../../store/selectors/page-content.selectors';
import { selectLoadingState } from '../../../store/selectors/page-loading.selectors';
import { LearnosityPlayerComponent } from '../../common/learnosity-player/learnosity-player.component';
import { PageComponent } from '../../common/page/page.component';
import { PageConfig } from '../../common/page/page.models';
import {
  TestletActionButtonsConfig,
  TestletActionButtonType,
} from '../../common/testlet-action-buttons/models/testlet-action-buttons.models';
import { TestletActionButtonsComponent } from '../../common/testlet-action-buttons/testlet-action-buttons.component';
import { BaseTestletHandler } from '../base-testlet-handler';

@Component({
  selector: 'picture-completion',
  templateUrl: './picture-completion.component.html',
  styleUrls: ['./picture-completion.component.scss', '../styles/testlet.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, LearnosityPlayerComponent, PageComponent, TestletActionButtonsComponent],
})
export class PictureCompletionComponent extends BaseTestletHandler implements OnDestroy, AfterViewInit {
  protected updatedAssessmentConfig = this.store.select(selectCurrentUpdateAssessmentState);
  protected displayActionButtons$: Observable<boolean> = this.store.select(selectDisplayActionButtons);
  protected testletActionButtonsConfig$: Observable<TestletActionButtonsConfig> = this.store
    .select(selectActionButtons)
    .pipe(
      map((buttons) => ({
        buttonTrackingContext: 'picture-completion',
        ...buttons,
      })),
    );
  protected pageConfig$: Observable<PageConfig> = this.store
    .select(selectLoadingState)
    .pipe(map((state) => ({ loading: state.loadingData.loading })));
  private readonly responseClickSubject = new Subject<Event>();
  private isNavigationEnabled = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    protected playerService: AssessmentPlayerService,
    protected store: Store<PageContentState>,
    private pageUtils: PageUtils,
    @Inject(APP_CONFIG) private readonly config: AppConfig,
  ) {
    super(playerService, store);
  }

  ngAfterViewInit() {
    // Set Event Listeners action methods here.
    this.eventHandlers = {
      questionLoadEventNotifier$: (itemPosition) => {
        this.setQuestionPageControls(itemPosition);
      },
      questionChangeEventNotifier$: (response) => {
        this.isNavigationEnabled = response.attemptedStatus === AssessmentAttemptedStatus.FullyAttempted;
      },
      testSubmitBeginNotifier$: () => {
        this.isNavigationEnabled = true;
      },
    };
    this.updatedAssessmentConfig.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((updateAssessment) => {
      if (
        updateAssessment != null &&
        !updateAssessment.loading &&
        updateAssessment.error == null &&
        updateAssessment.data?.config != null &&
        updateAssessment.data?.nextTestlet == 'Picture Completion'
      ) {
        const testlet = updateAssessment.data;
        this.initializeListeners(
          JSON.parse(updateAssessment.data.config),
          'PictureCompletion',
          testlet.sessionId,
          testlet.assessmentId,
        );
      } else {
        this.pageUtils.navigateToIntroductionPage(!updateAssessment?.loading);
      }
    });

    this.responseClickSubject
      .pipe(debounceTime(this.config.debounceTime), takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.handleDebouncedClick(event);
      });
    this.buttonClickSubject
      .pipe(debounceTime(this.config.debounceTime), takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        this.handleDebouncedButtonClick(type);
      });
  }

  ngOnDestroy() {
    this.onDestroy();
  }

  @HostListener('window:keyup', ['$event'])
  public keyUpEventListener(event: KeyboardEvent) {
    event.preventDefault();
    const isEnterKeyDown = event.key === ' ' || event.key === 'Enter';
    if (isEnterKeyDown) {
      (this.document.activeElement as HTMLElement)?.click();
    }
  }

  @HostListener('click', ['$event'])
  public responseClickHandler(event: Event) {
    this.responseClickSubject.next(event);
  }

  protected override handleDebouncedClick(event: Event): void {
    const targetElement = event.target as HTMLElement;
    const tagName = targetElement.tagName;
    if (tagName !== 'INPUT') {
      return;
    }
    if (this.playerService.isLastInstruction()) {
      this.store.dispatch(noNav());
    }
    if (this.isNavigationEnabled) {
      this.navigateToNextItem();
    }
  }

  protected readonly TestletType = TestletType;
}
